import { useEffect, useRef } from 'react';

interface UseMessageReadObserverOptions {
  /**
   * The list of message IDs rendered in the scroll container.
   * Used to retrigger the observer registration whenever the list changes.
   */
  messageIds: Array<number | undefined>;
  /**
   * Callback invoked when a message becomes visible enough to be considered read.
   */
  onVisible: (messageId: number) => void;
  /**
   * Whether the observer should run. Defaults to true.
   */
  enabled?: boolean;
  /**
   * Intersection ratio required before a message counts as read.
   * Defaults to 0.65 to match the previous behaviour.
   */
  threshold?: number;
  resetKey?: unknown;
  /**
   * Attribute selector used to identify the message elements.
   * Defaults to 'data-message-id'.
   */
  attributeSelector?: string;
}

/**
 * Sets up an IntersectionObserver that marks chat messages as read
 * once they are sufficiently visible within the scroll container.
 *
 * Returns the `ref` you should bind to the scrollable element.
 */
export function useMessageReadObserver(options: UseMessageReadObserverOptions) {
  const { messageIds, onVisible, enabled = true, threshold = 0.65, resetKey, attributeSelector = 'data-message-id' } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  const visibleMessagesRef = useRef<Set<number>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const onVisibleRef = useRef(onVisible);

  useEffect(() => {
    onVisibleRef.current = onVisible;
  }, [onVisible]);

  useEffect(() => {
    if (!enabled || !containerRef.current) {
      return;
    }

    visibleMessagesRef.current.clear();

    const observerOptions: IntersectionObserverInit = {
      root: containerRef.current,
      threshold: threshold,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const messageElement = entry.target as HTMLElement;
          const messageIdStr: string | null = messageElement.getAttribute('data-message-id');

          if (messageIdStr) {
            const messageId: number = Number(messageIdStr);

            if (!isNaN(messageId) && !visibleMessagesRef.current.has(messageId)) {
              visibleMessagesRef.current.add(messageId);
              onVisibleRef.current(messageId);
            }
          }
        }
      });
    }, observerOptions);

    observerRef.current = observer;

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [enabled, threshold, resetKey]);

  useEffect(() => {
    if (!enabled || !containerRef.current || !observerRef.current) {
      return;
    }

    const messageIdSet = new Set(messageIds.filter((id) => id !== undefined));

    const messageElements: Element[] = Array.from(containerRef.current.querySelectorAll(`[${attributeSelector}]`)).filter((element) => {
      const msgId: number = Number((element as HTMLElement).getAttribute(attributeSelector));
      return !isNaN(msgId) && messageIdSet.has(msgId);
    });

    messageElements.forEach((element) => {
      observerRef.current?.observe(element);
    });
  }, [messageIds, enabled]);

  return { containerRef };
}
